import {
  CompsFormItemConfig as FormItemConfig,
  type FooterButtonConfig,
} from "@ms/shared-components";
import { SpaceType as SpaceTypeEnum, SpaceStatus } from "../../../types/space";
import TagSelector from "../components/TagSelector";
// 空间类型选项配置
export const SPACE_TYPE_OPTIONS = Object.entries({
  [SpaceTypeEnum.BUILDING]: { text: "建筑", color: "orange", label: "建筑" },
  [SpaceTypeEnum.ZONE]: { text: "区域", color: "blue", label: "区域" },
  [SpaceTypeEnum.SPACE]: { text: "空间", color: "green", label: "空间" },
}).map(([key, config]) => ({
  value: key as SpaceTypeEnum,
  label: config.label,
}));

// 空间状态选项配置
export const SPACE_STATUS_OPTIONS = Object.entries({
  [SpaceStatus.ACTIVE]: { text: "启用", color: "green", label: "启用" },
  [SpaceStatus.INACTIVE]: { text: "禁用", color: "red", label: "禁用" },
  [SpaceStatus.MAINTENANCE]: {
    text: "维护中",
    color: "orange",
    label: "维护中",
  },
}).map(([key, config]) => ({
  value: key as SpaceStatus,
  label: config.label,
}));

// 表单验证规则配置
export const FORM_VALIDATION_RULES = {
  NAME: [
    { required: true, message: "请输入空间名称" },
    { max: 100, message: "名称长度不能超过100个字符" },
  ],
  TYPE: [{ required: true, message: "请选择空间类型" }],
  STATUS: [{ required: true, message: "请选择状态" }],
};

// 页面标题配置
export const PAGE_TITLES = {
  TABLE: "空间列表",
  SEARCH: "空间搜索",
  CREATE: "新建空间",
  EDIT: "编辑空间",
  ADD_CHILD: "添加子空间",
} as const;

// 空间表单配置
export const createSpaceFormItems = (editingSpace?: any): FormItemConfig[] => {
  // 判断是否来自酒店，如果是，则除了 tag 外其他字段都禁用
  const isFromHotel = editingSpace?.fromHotel === true;
  
  return [
  {
    name: "name",
    label: "空间名称",
    type: "input",
    rules: FORM_VALIDATION_RULES.NAME,
    props: {
      placeholder: "例如：3楼101会议室",
      allowClear: true,
      disabled: isFromHotel, // 来自酒店时禁用
    },
  },
  {
    name: "type",
    label: "空间类型",
    type: "select",
    rules: FORM_VALIDATION_RULES.TYPE,
    options: (() => {
      // 如果是添加子空间，根据父级类型确定子空间类型
      if (editingSpace?.parentId) {
        const parentType = editingSpace.type;
        if (parentType === SpaceTypeEnum.BUILDING) {
          return [{ value: SpaceTypeEnum.ZONE, label: "区域" }];
        } else if (parentType === SpaceTypeEnum.ZONE) {
          return [{ value: SpaceTypeEnum.SPACE, label: "空间" }];
        }
      }

      // 默认返回所有类型
      return SPACE_TYPE_OPTIONS;
    })(),
    props: {
      placeholder: "请选择空间类型",
      allowClear: false,
      disabled: !!editingSpace?.parentId || !editingSpace || !!editingSpace?.id || isFromHotel, // 添加子空间、新建根级空间或编辑时都禁用选择，来自酒店时也禁用
    },
  },
  {
    name: "parentId",
    label: "父级空间ID",
    type: "input",
    hidden: true, // 隐藏字段，用于传递parentId
    props: {
      style: { display: "none" },
    },
  },
  {
    name: "parentName",
    label: "上级空间",
    type: "input",
    hidden: !(editingSpace?.parentId && editingSpace?.id), // 只在编辑已有子空间时显示，添加子空间时不显示
    props: {
      placeholder: "上级空间名称",
      disabled: true,
      extra: "上级空间（只读）",
    },
  },
  {
    name: "status",
    label: "状态",
    type: "select",
    rules: FORM_VALIDATION_RULES.STATUS,
    options: SPACE_STATUS_OPTIONS,
    props: {
      placeholder: "请选择状态",
      allowClear: false,
      disabled: isFromHotel, // 来自酒店时禁用
    },
  },
  {
    name: "tagIds",
    label: "标签",
    type: "custom",
    customComponent: TagSelector,
    props: {
      placeholder: "请选择标签（可多选）",
    },
  },
  {
    name: "description",
    label: "描述",
    type: "textarea",
    props: {
      rows: 4,
      placeholder: "例如：面积50㎡，容纳20人，配备投影",
      maxLength: 500,
      showCount: true,
      extra: "可选，描述空间的用途、面积等信息",
      disabled: isFromHotel, // 来自酒店时禁用
    },
  },
];
};

// 表单默认值
export const getDefaultFormValues = (editingSpace?: any) => {
  // 确保所有字段都有明确的默认值
  const defaultValues: any = {
    name: undefined,
    type: undefined,
    parentId: undefined,
    parentName: undefined,
    status: SpaceStatus.ACTIVE,
    tagIds: [],
    description: undefined,
  };

  // 如果是编辑空间
  if (editingSpace?.id) {
    // 设置编辑时的默认值
    defaultValues.name = editingSpace.name;
    defaultValues.type = editingSpace.type;
    defaultValues.status = editingSpace.status;
    // 处理标签ID：从tags数组提取id，或直接使用tagIds
    defaultValues.tagIds =
      editingSpace.tagIds ||
      (editingSpace.tags ? editingSpace.tags.map((tag: any) => tag.id) : []);
    defaultValues.description = editingSpace.description;

    // 设置上级空间信息
    if (editingSpace.parentId) {
      defaultValues.parentId = editingSpace.parentId;
      defaultValues.parentName =
        editingSpace.parent?.name ||
        editingSpace.parentName ||
        editingSpace.parentId;
    }
  }
  // 如果是添加子空间（有parentId但没有id）
  else if (editingSpace?.parentId && !editingSpace?.id) {
    // 设置父级空间ID（用于提交）
    defaultValues.parentId = editingSpace.parentId;
    // 不设置parentName，添加子空间时不显示上级空间名称
    defaultValues.parentName = undefined;

    // 设置默认的空间类型
    const parentType = editingSpace.type;
    if (parentType === SpaceTypeEnum.BUILDING) {
      defaultValues.type = SpaceTypeEnum.ZONE;
    } else if (parentType === SpaceTypeEnum.ZONE) {
      defaultValues.type = SpaceTypeEnum.SPACE;
    }

    // 添加子空间时，标签和描述都为空
    defaultValues.tagIds = [];
    defaultValues.description = undefined;
  } else {
    // 新建根级空间时，默认设置为建筑
    defaultValues.type = SpaceTypeEnum.BUILDING;
  }

  return defaultValues;
};

// 表单标题配置
export const getFormTitle = (isEdit: boolean, isAddChild: boolean = false) =>
  isAddChild
    ? PAGE_TITLES.ADD_CHILD
    : isEdit
      ? PAGE_TITLES.EDIT
      : PAGE_TITLES.CREATE;

// 表单宽度配置
export const FORM_WIDTH = 600;

// 表单高度配置
export const FORM_HEIGHT = "auto";

// 表单布局配置
export const FORM_LAYOUT = {
  layout: "horizontal" as const,
  labelCol: { span: 6 },
  wrapperCol: { span: 16 },
};

// 表单弹窗按钮配置
export const createFormModalButtons = (handlers: {
  closeFormModal: () => void;
  handleFormSubmit: () => void;
}): FooterButtonConfig[] => [
  {
    key: "cancel",
    text: "取消",
    type: "default",
    onClick: handlers.closeFormModal,
  },
  {
    key: "submit",
    text: "确定",
    type: "primary",
    onClick: handlers.handleFormSubmit,
  },
];
